/**
 * @features/dashboard/components/DashboardSidebar.jsx
 * ---------------------------------------------------
 * Role-aware sidebar. Renders navigation items from `useDashboardNav()`.
 *
 *   • Desktop (≥lg): static vertical sidebar, sticky beside the content.
 *   • Tablet (md):   collapsible drawer triggered by the parent layout.
 *   • Mobile (<md):  HIDDEN by default — mobile uses the global bottom
 *                    navigation instead. The sidebar can still be rendered
 *                    inside a drawer if a page explicitly opts in.
 *
 * Visual styling deliberately mirrors the rest of the app: white card with
 * #E4E4E7 border, active item gets the brand-blue accent stripe. No new
 * branding tokens introduced.
 *
 * v2.71.27 — Initial.
 */

import React from "react";
import { NavLink } from "react-router-dom";
import { useDashboardNav } from "../hooks/useDashboardNav";

export function DashboardSidebar({ persona, className = "" }) {
  const items = useDashboardNav(persona);
  if (!items.length) return null;

  return (
    <nav
      aria-label="Dashboard navigation"
      className={`bg-white border border-[#E4E4E7] rounded-sm p-1.5 ${className}`}
      data-testid="dashboard-sidebar"
    >
      <ul className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.id}>
              <NavLink
                to={item.to}
                end
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 px-3 py-2 rounded-sm",
                    "text-[13px] font-medium tracking-tight transition-colors",
                    isActive
                      ? "bg-[#EEF4FF] text-[#0055FF] border-l-2 border-[#0055FF] pl-[10px]"
                      : "text-[#27272A] hover:bg-[#F4F4F5] border-l-2 border-transparent pl-[10px]",
                  ].join(" ")
                }
                data-testid={`dash-nav-${item.id}`}
              >
                {Icon ? <Icon size={15} strokeWidth={1.75} /> : null}
                <span className="flex-1">{item.label}</span>
                {typeof item.badge === "function" && item.badge() ? (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 bg-[#DC2626] text-white text-[10px] font-semibold rounded-full">
                    {item.badge()}
                  </span>
                ) : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
